import { SOCKET_URL } from '@/lib/constants/env';
import { mapActions, mapGetters } from 'vuex';
import { io } from 'socket.io-client';
import TokenStorage from '@/lib/token-storage';
import { NotificationService } from '@/modules/global-settings/system/services/notification.service';


const socketio = io(SOCKET_URL);
const notificationService = new NotificationService();
export default {
  data() {
    return {
      appName: 'delinquency-pro',
      additionalDataAvailable: false,
      useSocketEmitOnly: false
    };
  },

  created() {
    if (!this.useSocketEmitOnly) {
      socketio.on('connect', () => {
        console.log('SOCKET: CONNECTED');
        this.authenticateSocket();
      });

      socketio.on('disconnect', () => {
        console.log('SOCKET: DISCONNECTED');
        this.isSocketAuthenticated = false;
      });

      socketio.on('workload-updated', () => {
        console.log('Received "workload-updated" event from server.');

        if (typeof this.updateBadgeCount === 'function') {
          console.log('Calling updateBadgeCount().');
          this.updateBadgeCount();
        }
      });
      
      socketio.on('events', (event) => {
        if (event.app != this.appName) return;
        if (this.handleEventType) {
          this.handleEventType(event.type, event.data, event.message);
        }
      });

      socketio.on('notify', ({ data }) => {
        if (data.assigneeID == this.user.id && this.loadNotifications) {
          this.loadNotifications({ userId: data.assigneeID, appName: this.appName });
        }
      });
    }
  },

  computed: {
    ...mapGetters('authModule', ['user']),
  },

  watch: {
    user(newUser) {
      if (newUser?.id) {
        this.authenticateSocket();
      }
    },
  },

  methods: {
    ...mapActions('caseManagementModule', ['removeCase']),
    ...mapActions('userNotificationsModule', ['addNotification', 'loadNotifications']),
    ...mapActions('userNotificationsModule', ['userModule', 'fetchUser']),

    authenticateSocket() {
      if (this.user?.id && !this.isSocketAuthenticated) {
        console.log('Attempting to authenticate socket...');
        const token = TokenStorage.getToken();
        if (token) {
          socketio.emit('authenticate', token);
          this.isSocketAuthenticated = true;
        } else {
          console.warn('Socket authentication skipped: No auth token found.');
        }
      }
    },

    /**
     * Broadcast an app event to all online users
     * @param {*} data
     */
    emitEvent({ eventName, type, message, data }) {
      console.log('emitting...');
      
      socketio.emit( (eventName || 'events'), {
        app: this.appName,
        type,
        message,
        data
      });
    },

    async getDelinquencyNotificationConfiguration() {
      return notificationService
        .getConfigByModule('delinquency')
        .then(({ data }) => data?.configuration ?? null)
        .catch(() => null);
    },

    isInAppNotificationEnabled(configuration) {
      const inAppNotificationType = 'in app'.toLowerCase();
      return (
        configuration?.isActive === true &&
        configuration.notificationType?.some(
          (notificationType) =>
            notificationType?.toLowerCase() === inAppNotificationType
        ) === true
      );
    },

    /**
     * Display snack message
     * @param { string } title - Message title
     * @param { string } message - Message details
     */
    displaySnackMessage(title, message) {
      EventBus.$emit('SHOW_SNACK', {
        title,
        message,
        color: 'info'
      });
    },

    /**
     * Initiates the correct actions of the event type that has occurred
     * @param { 'termination' | 'unassignment' | 'closure' } type The event type that occurred
     * @param {*} event
     */
    handleEventType(type, data, message) {
      if (type === 'case escalated' || type === 'escalated employee notification') {
        this.handleCaseEscalationNotification(type, data, message);
        return;
      }

      if (type.includes('control action')) {
        this.handleControlActionAssignment(type, data, message);
        return;
      }

      if(type.includes('unassignment') || type.includes('closed')) {
        this.handleCaseUnassignmentOrTermination(type, data, message);
        return;
      }

      if(type.includes('assignment')) {
        this.handleCaseAssignment(type, data, message);
        return
      }
    },

    async handleControlActionAssignment(type, data, message) {
      if (data.assigneeID != this.user?.id) return;

      const isUnassignment = type.includes('unassignment');
      await this.addNotification({
        type,
        module: this.appName,
        recieverID: data.assigneeID,
        senderID: data.senderID,
        title: `Delinquency Control Action ${
          isUnassignment ? 'De-Assignment' : 'Assignment'
        }`,
        message,
        isRead: false
      });
      this.emitEvent({
        eventName: 'notify',
        data: { assigneeID: data.assigneeID }
      });
    },

    /**
     * Gets called when a case is assigned
     * @param {*} data
     */
    async handleCaseAssignment(type, data, message) {

      const isAssignedToCurrentUser = data.assigneeID == this.user?.id;
      
      if(isAssignedToCurrentUser) {
        await this.addNotification({
          type,
          module: this.appName,
          recieverID: data.assigneeID,
          senderID: data.senderID,
          title: 'Case Assignment',
          message,
          isRead: false
        })
        this.emitEvent({
          eventName: 'notify',
          data: { assigneeID: data.assigneeID }
        });
      }

      if(data?.caseDetails) {
        this.loadCaseDetails(data)
      }

    },

    async handleCaseEscalationNotification(type, data, message) {
      if (data.assigneeID != this.user?.id) return;

      await this.addNotification({
        type,
        module: this.appName,
        recieverID: data.assigneeID,
        senderID: data.senderID,
        title: 'Case Escalated',
        message,
        isRead: false
      });
      this.emitEvent({
        eventName: 'notify',
        data: { assigneeID: data.assigneeID }
      });
    },

    loadCaseDetails(data) {
      const caseDetails = data.caseDetails;
      const oldCaseId = data.oldCaseId;
      const caseIds = [caseDetails?.id, oldCaseId];

      const isAssignedToCurrentUser = caseDetails?.officerAssigned?.id == this.user?.id;

      //Only runs when the current user is in their workload
      if (this.cases && isAssignedToCurrentUser) {
        if (this.totalItems > 10) {
          this.additionalDataAvailable = true;
        } else if (this.addCase) {
          this.addCase({ caseDetails });
        }
        this.totalItems += 1;
        return;
      }

      //Update Case Information in Delinquency Pool
      if (this.tableData) {
        const index = this.tableData.findIndex((el) => caseIds.includes(el.id));
        if (index > -1) {
          this.tableData.splice(index, 1, caseDetails);
        }
        return;
      }

      //Check if a user who is not assigned to the case has the case in their workload,
      //and removre it
      if (!isAssignedToCurrentUser) {
        const index = this.cases?.findIndex((el) => caseIds.includes(el.id));
        if (index >= 0 && this.cases?.length) {
          this.removeCase({ caseId: caseDetails.id });
          this.totaltems -= 1;
        }

        //Check if the user who is not assigned to the case is on case file.
        //Note: this.selectedCase is from the case-mangemeent store
        if (this.selectedCase && caseIds.includes(this.selectedCase.id)) {
          const currentRoute = this.$route.name;
          const isReadOnly = !!this.$route?.params?.id;

          //Redirect user if the case file is not read only
          if (currentRoute.indexOf('case-management') != -1 && !isReadOnly) {
            this.$router.replace({ name: 'delinquency-pool' });
          }
        }
      }

    },

    /**
     * Gets called when a case is closed or unassigned
     * @param { 'termination' | 'unassignment' } type The event type that occurred
     * @param {*} data - the event data
     * @param { Boolean } isLoanPool - indicates if the user is currently on the loan pool page
     */
    async handleCaseUnassignmentOrTermination(type, data, message) {
      const isAssignedToCurrentUser = data.assigneeID == this.user.id;
      const caseDetails = data.caseDetails;
      
      if(isAssignedToCurrentUser) {
        await this.addNotification({
          type,
          module: this.appName,
          recieverID:  data.assigneeID,
          senderID: data.senderID,
          title: `Case ${type.includes('closed') ? 'Closed' : 'De-Assignment'}`,
          message,
          isRead: false
        })
        this.emitEvent({
          eventName: 'notify',
          data: { assigneeID: data.assigneeID }
        });
      }
      
      //Remove case from table if was closed or update case if was unassigned.
      if (this.tableData) {
        const index = this.tableData.findIndex(
          (el) => el.id == caseDetails?.id
        );
        if (index > -1) {
          if (type === 'closed') {
            this.tableData.splice(index, 1);
            this.totalItems -= 1;
          } else {
            caseDetails.officerAssigned = null;
            caseDetails.status = 'unassigned';
            this.tableData.splice(index, 1, caseDetails);
          }
        }

        return;
      }

      if (this.cases?.length) {
        this.removeCase({ caseId: caseDetails?.id });
        if (this.totalItems) {
          this.totalItems -= 1;
        }
      }

      //Check if a user who is not assigned is on the case a redirect them away
      if (!isAssignedToCurrentUser) {
        //Check if the user who is not assigned to the case is on case file.
        //Note: this.selectedCase is from the case-mangemeent store
        if (this.selectedCase && this.selectedCase.id === caseDetails?.id) {
          const currentRoute = this.$route.name;
          const isReadOnly = !!this.$route?.params?.id;

          //Redirect user if the case file is not read only
          if (currentRoute.indexOf('case-management') != -1 && !isReadOnly) {
            this.$router.replace({ name: 'delinquency-pool' });
          }
        }
      }

    },


    async emitChargeOffCaseAssignment(assignee) {
      const isAssignedUser = this.user.id == assignee.id;
      this.emitEvent({
        type: 'charge-off assignment',
        message: isAssignedUser ? `You assigned a charge-off case to your workload`  : `${this.user.username} assigned a charge-off case to your workload.`,
        data: {
          senderID: this.user.id,
          assigneeID: assignee.id
        }
      });
    },

    async emitChargeOffCaseUnassignment(assignee) {
      const isAssignedUser = this.user.id == assignee.id;
      this.emitEvent({
        type: 'charge-off unassignment',
        message: isAssignedUser ? `You de-assigned a charge-off case from your workload` : `${ this.user.username } has de-assigned a charge-off case from your workload.`,
        data: {
          senderID: this.user.id,
          assigneeID: assignee.id,
        }
      });
    },


    async emitForeclosureCaseAssignment(assignee){
      if(this.user.id !== assignee.id) {
        await this.addNotification({
          type: 'foreclosure assignment',
          module: this.appName,
          recieverID: assignee.id,
          senderID: this.user.id,
          title: 'Task Assignment',
          message: `${ this.user.username } has assigned a foreclosure case to you. Kindly check your workload to confirm.`,
          isRead: false
        })
        this.emitEvent({
          eventName: 'notify',
          type: 'foreclosure assignment',
          data: {
            username: this.user.username,
            assigneeID: assignee.id
          }
        });
      }
    },

    async emitForeclosureCaseUnassignment(assignee) {
      if(this.user.id !== assignee.id) {
        await this.addNotification({
          type: 'foreclosure unassignment',
          module: this.appName,
          recieverID: assignee.id,
          senderID: this.user.id,
          title: 'Foreclosure Case De-Assignement',
          message: `${ this.user.username } has de-assigned a foreclosure case from your workload.`,
          isRead: false
        })
      }
      this.emitEvent({
        eventName: 'notify',
        type: 'foreclosure unassignment',
        data: {
          username: this.user.username,
          assigneeID
        }
      });
    },


    /**
     * Emit events
     *
     * @param { Object } caseDetails - the case details object
     **/
    async emitLoanClosedEvent(caseDetails) {
      const configurations =
        await this.getDelinquencyNotificationConfiguration();
      if (
        !this.isInAppNotificationEnabled(configurations?.delinquencyCaseClosure)
      )
        return;

      this.emitEvent({
        type: 'case closed',
        message: `${this.user.username} closed a case assigned you.`,
        data: {
          senderID: this.user.id,
          assigneeID: caseDetails?.officerAssigned?.id,
          caseDetails: caseDetails ?? {}
        }
      });
    },

    /**
     * Emit event to all users that loan was deleted
     *
     * @param { Object } caseDetails - the case details object
     **/
    emitLoanDeletionEvent(caseDetails) {
      this.emitEvent({
        type: 'termination',
        message: `${this.user.username} permanently deleted a case assigned you.`,
        data: {
          ...caseDetails
        }
      });
    },

    /**
     * Emit event to notify the assigned user that a loan was assigned to them
     *
     * @param { Object } caseDetails - the case object
     * @param { String } oldCaseId - the id of the current to be assigned
     * @param { Object } assignedUser - the user to be assigned
     **/
    async emitAssignCaseToUserEvent(caseDetails, oldCaseId, assignedUser) {
      const configurations =
        await this.getDelinquencyNotificationConfiguration();
      if (
        !this.isInAppNotificationEnabled(
          configurations?.delinquencyNewCaseAssignment
        )
      )
        return;

      const isAssignedUser = this.user.id == assignedUser.id;
      this.emitEvent({
        type: 'case assignment',
        message: isAssignedUser ? `You assigned a case to your workload.` : `${this.user.username} assigned a case to your workload.`,
        data: {
          senderID: this.user.id,
          assigneeID: caseDetails?.officerAssigned?.id,
          caseDetails,
          oldCaseId
        }
      });
    },

        /**
     * Emit event to all users that the loan was unassigned
     *
     * @param { Object } caseDetails - the details of the case
     **/
    async emitLoanUnassignmentEvent(caseDetails, assignedUserID) {
      const configurations =
        await this.getDelinquencyNotificationConfiguration();
      if (
        !this.isInAppNotificationEnabled(
          configurations?.delinquencyNewCaseAssignment
        )
      )
        return;

      const isAssignedUser = this.user.id == assignedUserID;
      this.emitEvent({
        type: 'case unassignment',
        message: isAssignedUser ? `You de-assigned a case from your workload.` : `${this.user.username} de-assigned a case from your workload.`,
        data: {
          senderID: this.user.id,
          assigneeID: assignedUserID,
          caseDetails
        }
      });
    },

    async emitControlActionAssignmentEvent(assignee, controlAction) {
      const configurations =
        await this.getDelinquencyNotificationConfiguration();
      if (
        !this.isInAppNotificationEnabled(
          configurations?.delinquencyTaskAssignment
        ) ||
        !assignee?.id ||
        this.user.id == assignee?.id
      )
        return;

      this.emitEvent({
        type: 'control action assignment',
        message: `${this.user.username} assigned the Delinquency Control Action "${controlAction.actionName}" to you.`,
        data: {
          senderID: this.user.id,
          assigneeID: assignee.id,
          controlActionID: controlAction.id
        }
      });
    },

    async emitControlActionUnassignmentEvent(assignee, controlAction) {
      const configurations =
        await this.getDelinquencyNotificationConfiguration();
      if (
        !this.isInAppNotificationEnabled(
          configurations?.delinquencyTaskAssignment
        ) ||
        !assignee?.id ||
        this.user.id == assignee?.id
      )
        return;

      this.emitEvent({
        type: 'control action unassignment',
        message: `${this.user.username} de-assigned the Delinquency Control Action "${controlAction.actionName}" from you.`,
        data: {
          senderID: this.user.id,
          assigneeID: assignee.id,
          controlActionID: controlAction.id
        }
      });
    },

    async emitCaseEscalationEvents(escalatee, caseDetails) {
      const configurations = await this.getDelinquencyNotificationConfiguration();
      const caseEscalatedConfiguration = configurations?.delinquencyCaseEscalation?.find(
        (configuration) => configuration.actionName?.toLowerCase() === 'case escalated'.toLowerCase()
      );
      const escalatedEmployeeConfiguration = configurations?.delinquencyCaseEscalation?.find(
        (configuration) =>
          configuration.actionName?.toLowerCase() === 'escalated employee notification'.toLowerCase()
      );
      const accountNumber = caseDetails?.delinquencyCase?.loan?.mainAccountNumber;

      if (this.isInAppNotificationEnabled(caseEscalatedConfiguration)) {
        this.emitEvent({
          type: 'case escalated',
          message: `You escalated delinquency case ${accountNumber || ''} to ${escalatee?.username || 'the selected employee'}.`.trim(),
          data: {
            senderID: this.user.id,
            assigneeID: this.user.id,
            caseID: caseDetails?.id
          }
        });
      }

      if (this.isInAppNotificationEnabled(escalatedEmployeeConfiguration) && escalatee?.id) {
        this.emitEvent({
          type: 'escalated employee notification',
          message: `${this.user.username} escalated delinquency case ${accountNumber || ''} to you.`.trim(),
          data: {
            senderID: this.user.id,
            assigneeID: escalatee.id,
            caseID: caseDetails?.id
          }
        });
      }
    }
  }
};
